/**
 * WorkOrdersPage — Module Travaux
 * Gestion des bons de travaux par structure.
 */

import React, { useState, useEffect } from 'react';
import {
  Hammer, Plus, Search, X, ChevronDown, ChevronRight,
  Trash2, Edit, FileText, AlertTriangle, CheckCircle,
  Clock, Upload, Paperclip, Building, Calendar,
  MapPin, User, Loader
} from 'lucide-react';
import {
  getWorkOrders, getWorkOrdersMultiStructures, getWorkOrderCategories,
  createWorkOrder, updateWorkOrder, deleteWorkOrder,
  uploadWorkOrderFile, deleteWorkOrderFile,
  getRooms,
} from '../api';
import { useStructure } from '../contexts/StructureContext';
import { usePersonContext } from '../contexts/PersonContext';
import { useToast } from '../contexts/NotificationContext';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Constantes ───────────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  faible:  { label: 'Faible',  color: 'bg-gray-100 text-gray-600' },
  normale: { label: 'Normale', color: 'bg-blue-100 text-blue-700' },
  haute:   { label: 'Haute',   color: 'bg-orange-100 text-orange-700' },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-700' },
};

const STATUS_CONFIG = {
  nouveau:  { label: 'Nouveau',    color: 'bg-gray-100 text-gray-600', icon: Clock },
  planifié: { label: 'Planifié',   color: 'bg-blue-100 text-blue-700', icon: Calendar },
  en_cours: { label: 'En cours',   color: 'bg-yellow-100 text-yellow-700', icon: Hammer },
  terminé:  { label: 'Terminé',    color: 'bg-green-100 text-green-700', icon: CheckCircle },
  annulé:   { label: 'Annulé',     color: 'bg-red-100 text-red-600', icon: X },
};

const EMPTY_FORM = {
  categoryId: '', title: '', description: '', location: '',
  priority: 'normale', status: 'nouveau', dueDate: '', interventionDate: '',
  reportedBy: '', assignedTo: '', company: '', estimatedCost: '', actualCost: '', notes: '',
  structureId: '',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtMoney = (v) => v == null ? '' : `${parseFloat(v).toFixed(2)} €`;
const isOverdue = (wo) =>
  wo.dueDate && wo.status !== 'terminé' && wo.status !== 'annulé' &&
  new Date(wo.dueDate) < new Date();

// ─── Composant ────────────────────────────────────────────────────────────────

const WorkOrdersPage = ({ showAllStructures = false, navPayload, onNavPayloadConsumed }) => {
  const { currentStructure, structures } = useStructure();
  const { currentPerson } = usePersonContext();
  const toast = useToast();
  const [workOrders, setWorkOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [selectedWO, setSelectedWO] = useState(null);
  const [collapsedStructures, setCollapsedStructures] = useState({});
  const [uploadingFile, setUploadingFile] = useState(null);

  const isMulti = showAllStructures && structures.length >= 2;

  useEffect(() => {
    if (currentStructure) fetchData();
  }, [currentStructure, showAllStructures]);

  useEffect(() => {
    if (currentStructure) {
      getRooms(currentStructure.id).then(r => setRooms(r.data)).catch(() => setRooms([]));
    }
  }, [currentStructure]);

  // Auto-ouverture fiche travaux depuis recherche globale (navPayload.workOrderId)
  useEffect(() => {
    if (!navPayload?.workOrderId || workOrders.length === 0) return;
    const wo = workOrders.find(w => w.id === navPayload.workOrderId);
    if (wo) {
      setSelectedWO(wo);
      onNavPayloadConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navPayload, workOrders]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const structureIds = structures.map(s => s.id);
      const [woRes, catRes] = await Promise.all([
        isMulti
          ? getWorkOrdersMultiStructures(structureIds)
          : getWorkOrders(currentStructure.id),
        getWorkOrderCategories(),
      ]);
      setWorkOrders(woRes.data);
      setCategories(catRes.data);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      toast.error('Erreur lors du chargement des bons de travaux');
    } finally {
      setLoading(false);
    }
  };

  const getPersonName = () =>
    currentPerson ? `${currentPerson.firstName} ${currentPerson.lastName}`.trim() : '';

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const openNewForm = () => {
    setForm({ ...EMPTY_FORM, reportedBy: getPersonName() });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (wo) => {
    setForm({
      categoryId: wo.categoryId,
      title: wo.title,
      description: wo.description || '',
      location: wo.location || '',
      priority: wo.priority,
      status: wo.status,
      dueDate: wo.dueDate ? wo.dueDate.split('T')[0] : '',
      interventionDate: wo.interventionDate ? wo.interventionDate.split('T')[0] : '',
      reportedBy: wo.reportedBy || '',
      assignedTo: wo.assignedTo || '',
      company: wo.company || '',
      estimatedCost: wo.estimatedCost || '',
      actualCost: wo.actualCost || '',
      notes: wo.notes || '',
      structureId: wo.structureId || '',
    });
    setEditingId(wo.id);
    setShowForm(true);
    setSelectedWO(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.categoryId || !form.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        structureId: form.structureId || currentStructure.id,
        location: form.location === '__autre__' ? null : (form.location || null),
        dueDate: form.dueDate || null,
        interventionDate: form.interventionDate || null,
        estimatedCost: form.estimatedCost || null,
        actualCost: form.actualCost || null,
      };
      if (editingId) {
        const res = await updateWorkOrder(editingId, payload);
        setWorkOrders(prev => prev.map(wo => wo.id === editingId ? res.data : wo));
        toast.success('Bon de travaux modifie');
      } else {
        await createWorkOrder(payload);
        await fetchData();
        toast.success('Bon de travaux cree');
      }
      resetForm();
    } catch (error) {
      console.error('Error saving work order:', error);
      toast.error('Erreur lors de la sauvegarde du bon de travaux');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce bon de travaux ?')) return;
    try {
      await deleteWorkOrder(id);
      setWorkOrders(prev => prev.filter(wo => wo.id !== id));
      if (selectedWO?.id === id) setSelectedWO(null);
      toast.success('Bon de travaux supprime');
    } catch (error) {
      console.error('Error deleting work order:', error);
      toast.error('Erreur lors de la suppression du bon de travaux');
    }
  };

  const handleStatusChange = async (wo, newStatus) => {
    try {
      const res = await updateWorkOrder(wo.id, { status: newStatus });
      setWorkOrders(prev => prev.map(w => w.id === wo.id ? res.data : w));
      if (selectedWO?.id === wo.id) setSelectedWO(res.data);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleUploadFile = async (woId, fileType, file) => {
    setUploadingFile(`${woId}-${fileType}`);
    try {
      const res = await uploadWorkOrderFile(woId, fileType, file);
      setWorkOrders(prev => prev.map(w => w.id === woId ? res.data : w));
      if (selectedWO?.id === woId) setSelectedWO(res.data);
      toast.success('Document uploade');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erreur lors de l\'upload du document');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleDeleteFile = async (woId, fileType) => {
    if (!window.confirm('Supprimer ce fichier ?')) return;
    try {
      const res = await deleteWorkOrderFile(woId, fileType);
      setWorkOrders(prev => prev.map(w => w.id === woId ? res.data : w));
      if (selectedWO?.id === woId) setSelectedWO(res.data);
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Erreur lors de la suppression du document');
    }
  };

  const toggleStructure = (name) =>
    setCollapsedStructures(prev => ({ ...prev, [name]: !prev[name] }));

  // Filtrage
  const filtered = workOrders.filter(wo => {
    if (searchText && !wo.title.toLowerCase().includes(searchText.toLowerCase()) &&
        !(wo.description || '').toLowerCase().includes(searchText.toLowerCase())) return false;
    if (filterStatus && wo.status !== filterStatus) return false;
    if (filterPriority && wo.priority !== filterPriority) return false;
    if (filterCategory && wo.categoryId !== filterCategory) return false;
    return true;
  });

  // Groupement multi-structures
  const grouped = isMulti
    ? filtered.reduce((acc, wo) => {
        const key = wo.structureName || wo.structureId;
        if (!acc[key]) acc[key] = [];
        acc[key].push(wo);
        return acc;
      }, {})
    : null;

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  const renderWOCard = (wo) => {
    const statusCfg = STATUS_CONFIG[wo.status] || STATUS_CONFIG.nouveau;
    const StatusIcon = statusCfg.icon;
    const priorityCfg = PRIORITY_CONFIG[wo.priority] || PRIORITY_CONFIG.normale;
    const overdue = isOverdue(wo);

    return (
      <div
        key={wo.id}
        onClick={() => setSelectedWO(wo)}
        className={`bg-white rounded-xl border cursor-pointer transition-all hover:shadow-md p-4 ${
          overdue ? 'border-red-200' : selectedWO?.id === wo.id ? 'border-primary shadow-md' : 'border-gray-200'
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.color}`}>
                <StatusIcon size={11} />{statusCfg.label}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityCfg.color}`}>
                {priorityCfg.label}
              </span>
              {overdue && (
                <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                  <AlertTriangle size={11} />En retard
                </span>
              )}
            </div>
            <p className="font-semibold text-gray-900 mt-1 text-sm leading-tight">{wo.title}</p>
          </div>
          <span className="text-xs text-gray-400 shrink-0">{wo.category?.name}</span>
        </div>

        {wo.description && (
          <p className="text-xs text-gray-500 line-clamp-2 mb-2">{wo.description}</p>
        )}

        <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
          {wo.location && <span className="flex items-center gap-1"><MapPin size={11} />{wo.location}</span>}
          {wo.dueDate && (
            <span className={`flex items-center gap-1 ${overdue ? 'text-red-500 font-medium' : ''}`}>
              <Calendar size={11} />Échéance : {fmtDate(wo.dueDate)}
            </span>
          )}
          {wo.assignedTo && <span className="flex items-center gap-1"><User size={11} />{wo.assignedTo}</span>}
          {(wo.invoiceUrl || wo.reportUrl) && <Paperclip size={11} className="text-primary" />}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" text="Chargement..." />
      </div>
    );
  }

  return (
    <div className="flex h-full gap-0">
      {/* Liste principale */}
      <div className={`flex flex-col flex-1 min-w-0 ${selectedWO ? 'hidden lg:flex' : ''}`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Hammer size={20} className="text-primary" />
              Travaux
              <span className="text-sm font-normal text-gray-400">({filtered.length})</span>
            </h1>
            <button
              onClick={openNewForm}
              className="flex items-center gap-1.5 bg-primary text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-dark transition"
            >
              <Plus size={15} />Nouveau
            </button>
          </div>

          {/* Filtres */}
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-40">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Rechercher..." value={searchText} onChange={e => setSearchText(e.target.value)} />
            </div>
            <select className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none"
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Tous statuts</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none"
              value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
              <option value="">Toutes priorités</option>
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none"
              value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              <option value="">Toutes catégories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        {/* Liste des travaux */}
        <div className="flex-1 overflow-y-auto p-4">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Hammer size={40} className="mb-3 opacity-30" />
              <p className="text-sm">Aucun bon de travaux</p>
              <button onClick={openNewForm}
                className="mt-3 text-primary text-sm font-medium hover:underline">
                + Créer le premier
              </button>
            </div>
          ) : isMulti ? (
            <div className="space-y-4">
              {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([structureName, wos]) => (
                <div key={structureName}>
                  <button
                    onClick={() => toggleStructure(structureName)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-primary/10 hover:bg-primary/15 rounded-t-lg transition-colors border-l-4 border-primary"
                  >
                    <div className="flex items-center gap-2">
                      {collapsedStructures[structureName] ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                      <Building size={16} className="text-primary" />
                      <span className="font-bold text-gray-900">{structureName}</span>
                      <span className="text-sm text-gray-500">({wos.length})</span>
                    </div>
                  </button>
                  {!collapsedStructures[structureName] && (
                    <div className="bg-white rounded-b-lg shadow-sm border border-t-0 border-gray-200 p-3 space-y-2">
                      {wos.map(wo => renderWOCard(wo))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(wo => renderWOCard(wo))}
            </div>
          )}
        </div>
      </div>

      {/* Panneau détail */}
      {selectedWO && (
        <div className="w-full lg:w-96 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900 text-sm truncate">{selectedWO.title}</h3>
            <button onClick={() => setSelectedWO(null)} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-5 text-sm">
            {/* Badges statut + priorité */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_CONFIG).map(([k, v]) => {
                const Icon = v.icon;
                return (
                  <button key={k}
                    onClick={() => handleStatusChange(selectedWO, k)}
                    className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium border transition cursor-pointer ${
                      selectedWO.status === k
                        ? v.color + ' border-transparent'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={11} />{v.label}
                  </button>
                );
              })}
            </div>

            {/* Infos */}
            <div className="space-y-2 text-xs text-gray-600">
              {[
                ['Catégorie', selectedWO.category?.name],
                ['Priorité', PRIORITY_CONFIG[selectedWO.priority]?.label],
                ['Lieu', selectedWO.location],
                ['Signalé par', selectedWO.reportedBy],
                ['Intervention', selectedWO.interventionDate ? fmtDate(selectedWO.interventionDate) : null],
                ['Échéance', fmtDate(selectedWO.dueDate)],
                ['Responsable', selectedWO.assignedTo],
                ['Entreprise', selectedWO.company],
                ['Coût estimé', selectedWO.estimatedCost ? fmtMoney(selectedWO.estimatedCost) : null],
                ['Coût réel', selectedWO.actualCost ? fmtMoney(selectedWO.actualCost) : null],
                ['Terminé le', selectedWO.completedAt ? fmtDate(selectedWO.completedAt) : null],
              ].filter(([, v]) => v).map(([l, v]) => (
                <div key={l} className="flex gap-2">
                  <span className="text-gray-400 w-28 shrink-0">{l}</span>
                  <span className="font-medium text-gray-800">{v}</span>
                </div>
              ))}
            </div>

            {selectedWO.description && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Description</p>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-xs">{selectedWO.description}</p>
              </div>
            )}

            {selectedWO.notes && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Notes</p>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-xs">{selectedWO.notes}</p>
              </div>
            )}

            {/* Documents */}
            <div className="border-t pt-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Documents</p>
                <div className="space-y-2">
                  {(['invoice', 'report']).map(ft => {
                    const urlField = ft === 'invoice' ? 'invoiceUrl' : 'reportUrl';
                    const nameField = ft === 'invoice' ? 'invoiceName' : 'reportName';
                    const label = ft === 'invoice' ? 'Facture' : 'Compte-rendu';
                    const url = selectedWO[urlField];
                    const name = selectedWO[nameField];
                    const uploading = uploadingFile === `${selectedWO.id}-${ft}`;

                    return (
                      <div key={ft} className="flex items-center gap-2">
                        {url ? (
                          <>
                            <a href={url} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1.5 text-primary hover:underline text-xs flex-1 truncate">
                              <FileText size={13} />{name || label}
                            </a>
                            <button onClick={() => handleDeleteFile(selectedWO.id, ft)}
                              className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                          </>
                        ) : (
                          <label className={`flex items-center gap-1.5 cursor-pointer text-xs text-gray-500 hover:text-primary ${uploading ? 'opacity-50' : ''}`}>
                            {uploading ? <Loader size={13} className="animate-spin" /> : <Upload size={13} />}
                            {label}
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                              onChange={e => e.target.files[0] && handleUploadFile(selectedWO.id, ft, e.target.files[0])} />
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
          </div>

          {/* Actions */}
          <div className="border-t border-gray-200 p-3 flex gap-2">
            <button onClick={() => handleEdit(selectedWO)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">
              <Edit size={14} />Modifier
            </button>
            <button onClick={() => handleDelete(selectedWO.id)}
              className="flex items-center justify-center gap-1.5 px-3 py-2 border border-red-200 rounded-lg text-sm text-red-500 hover:bg-red-50 transition">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Modal création/édition */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">
                {editingId ? 'Modifier le bon de travaux' : 'Nouveau bon de travaux'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {isMulti && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Structure <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={form.structureId} onChange={e => setForm(f => ({ ...f, structureId: e.target.value }))} required>
                      <option value="">Sélectionner une structure</option>
                      {structures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titre <span className="text-red-500">*</span></label>
                  <input className={inputCls} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie <span className="text-red-500">*</span></label>
                  <select className={inputCls} value={form.categoryId} onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))} required>
                    <option value="">Sélectionner...</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                  <select className={inputCls} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                    {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select className={inputCls} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lieu</label>
                  {rooms.length > 0 ? (() => {
                    const roomNames = rooms.map(r => r.name);
                    const inList = roomNames.includes(form.location);
                    const selectVal = form.location === '' ? '' : inList ? form.location : '__autre__';
                    return (
                      <>
                        <select className={inputCls} value={selectVal}
                          onChange={e => {
                            if (e.target.value === '__autre__') {
                              setForm(f => ({ ...f, location: '__autre__' }));
                            } else {
                              setForm(f => ({ ...f, location: e.target.value }));
                            }
                          }}>
                          <option value="">— Sélectionner —</option>
                          {rooms.map(r => (
                            <option key={r.id} value={r.name}>{r.name}{r.area ? ` (${r.area} m²)` : ''}</option>
                          ))}
                          <option value="__autre__">Autre / libre</option>
                        </select>
                        {(!inList && form.location !== '') && (
                          <input className={`${inputCls} mt-1`} placeholder="Préciser le lieu..."
                            value={form.location === '__autre__' ? '' : form.location}
                            autoFocus={form.location === '__autre__'}
                            onChange={e => setForm(f => ({ ...f, location: e.target.value || '__autre__' }))}
                          />
                        )}
                      </>
                    );
                  })() : (
                    <input className={inputCls} placeholder="Ex : Cuisine, Salle de bain..." value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Signalé par</label>
                  <input className={inputCls} value={form.reportedBy} onChange={e => setForm(f => ({ ...f, reportedBy: e.target.value }))}
                    placeholder="Personne à l'origine du signalement" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'intervention</label>
                  <input type="date" className={inputCls} value={form.interventionDate} onChange={e => setForm(f => ({ ...f, interventionDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Échéance</label>
                  <input type="date" className={inputCls} value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsable</label>
                  <input className={inputCls} value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
                  <input className={inputCls} value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coût estimé (€)</label>
                  <input type="number" step="0.01" className={inputCls} value={form.estimatedCost} onChange={e => setForm(f => ({ ...f, estimatedCost: e.target.value }))} />
                </div>
                {editingId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Coût réel (€)</label>
                    <input type="number" step="0.01" className={inputCls} value={form.actualCost} onChange={e => setForm(f => ({ ...f, actualCost: e.target.value }))} />
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea className={inputCls} rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes internes</label>
                  <textarea className={inputCls} rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={resetForm}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                  Annuler
                </button>
                <button type="submit" disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50">
                  {saving && <Loader size={14} className="animate-spin" />}
                  {editingId ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FAB mobile */}
      {!showForm && (
        <button
          onClick={openNewForm}
          className="lg:hidden fixed bottom-6 right-6 z-40 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition"
        >
          <Plus size={22} />
        </button>
      )}
    </div>
  );
};

export default WorkOrdersPage;
